import { api } from "@/lib/api";
import type {
  CreateDownloadRewriteRequest,
  CreateIndexerRequest,
  CreateRewriteRequest,
  IndexerDownloadRewriteRow,
  IndexerPreset,
  IndexerQueryRewriteRow,
  IndexerRow,
  IndexerSearchRow,
  IndexerStats,
  TestIndexerResponse,
  UpdateDownloadRewriteRequest,
  UpdateIndexerRequest,
  UpdateRewriteRequest,
} from "./types";

export const indexersApi = {
  list: () => api<IndexerRow[]>("/api/v1/indexers"),
  /**
   * Curated preset library (#34) — well-known public Newznab/Torznab
   * providers the "Add indexer" modal offers as one-click cards.
   */
  presets: () => api<IndexerPreset[]>("/api/v1/indexer-presets"),
  get: (id: number) => api<IndexerRow>(`/api/v1/indexers/${id}`),
  create: (body: CreateIndexerRequest) =>
    api<IndexerRow>("/api/v1/indexers", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  update: (id: number, body: UpdateIndexerRequest) =>
    api<IndexerRow>(`/api/v1/indexers/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  remove: (id: number) =>
    api<void>(`/api/v1/indexers/${id}`, { method: "DELETE" }),
  test: (id: number) =>
    api<TestIndexerResponse>(`/api/v1/indexers/${id}/test`, {
      method: "POST",
    }),
  /**
   * `POST /api/v1/indexers/:id/reset` — re-enable an indexer that
   * auto-disabled itself after `consecutive_failures` hit the
   * threshold. Clears `auto_disabled_at`, `backoff_until`,
   * `consecutive_failures`, and flips `enabled` back on. Used by the
   * row-level "Re-enable" button on the Indexers settings page when
   * the upstream's recovered but nothing automatically un-disabled
   * the row.
   */
  reset: (id: number) =>
    api<IndexerRow>(`/api/v1/indexers/${id}/reset`, { method: "POST" }),
  /**
   * Test an unsaved indexer configuration. Used by the Add / Edit modal
   * so users can validate before persisting.
   */
  testConfig: (body: CreateIndexerRequest) =>
    api<TestIndexerResponse>("/api/v1/indexers/test", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  /**
   * Recent search history rows (newest first) for the given indexer.
   * Limit is clamped server-side to [1, 500]; defaults to 100.
   */
  searches: (id: number, limit = 100) =>
    api<IndexerSearchRow[]>(
      `/api/v1/indexers/${id}/searches?limit=${limit}`,
    ),
  /**
   * 24h aggregate (total queries, success rate, avg response time,
   * zero-result count) for the given indexer.
   */
  stats: (id: number) => api<IndexerStats>(`/api/v1/indexers/${id}/stats`),
  /**
   * Per-indexer query-rewrite rules (#38). Returned ordered by `ord`
   * ascending — the same order they're applied to the outbound query.
   */
  rewritesList: (id: number) =>
    api<IndexerQueryRewriteRow[]>(`/api/v1/indexers/${id}/rewrites`),
  rewritesCreate: (id: number, body: CreateRewriteRequest) =>
    api<IndexerQueryRewriteRow>(`/api/v1/indexers/${id}/rewrites`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  rewritesUpdate: (
    id: number,
    rid: number,
    body: UpdateRewriteRequest,
  ) =>
    api<IndexerQueryRewriteRow>(`/api/v1/indexers/${id}/rewrites/${rid}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  rewritesDelete: (id: number, rid: number) =>
    api<void>(`/api/v1/indexers/${id}/rewrites/${rid}`, {
      method: "DELETE",
    }),
  /**
   * Per-indexer download-URL rewrite rules (#42). Returned ordered by
   * `ord` ascending — the same order they're applied to each release's
   * `download_url` just before the download client fetches it.
   */
  downloadRewritesList: (id: number) =>
    api<IndexerDownloadRewriteRow[]>(
      `/api/v1/indexers/${id}/download-rewrites`,
    ),
  downloadRewritesCreate: (id: number, body: CreateDownloadRewriteRequest) =>
    api<IndexerDownloadRewriteRow>(`/api/v1/indexers/${id}/download-rewrites`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  downloadRewritesUpdate: (
    id: number,
    rid: number,
    body: UpdateDownloadRewriteRequest,
  ) =>
    api<IndexerDownloadRewriteRow>(
      `/api/v1/indexers/${id}/download-rewrites/${rid}`,
      {
        method: "PATCH",
        body: JSON.stringify(body),
      },
    ),
  downloadRewritesDelete: (id: number, rid: number) =>
    api<void>(`/api/v1/indexers/${id}/download-rewrites/${rid}`, {
      method: "DELETE",
    }),
};
